import React from 'react'
import { Control, Controller, UseFormHandleSubmit } from 'react-hook-form'
import { InferType } from 'yup'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { captureException } from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { chatMessageSchema } from '../../../forms/schemas/chat_message'
import { Button, ButtonVariant } from '../../common/Button'
import { NChat } from '../../../interfaces/services/chat.interfaces'
import { RecipientRow } from '../../../generated/api/chat-recipients'
import { ChatHeaderState, chatHeaderStateAtom } from '../atom'
import { TextArea } from '../../common/TextArea'
import { IconChoices } from '../../common/Icon'
import { ProcessedMedia } from '../../../types/image-upload'
import { MessageBarImages } from './MessageBarImages'
import { MessageBarUpload } from './MessageBarUpload'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import {
  SendRoomMessageBody,
  chatConversationsQueryKey,
  createRoom,
  sendRoomMessage,
} from '../../../api/chat'
import { getUserFullName } from '../../../helpers/user'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { useAuth } from '../../../hooks/useAuth'
import { userQueries } from '../../../api/users'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { recipientFullName } from '../StartNewChatHeader/recipientOption'

interface ChatMessageBarProps {
  handleSubmit: UseFormHandleSubmit<InferType<typeof chatMessageSchema>>
  disabled: boolean
  control: Control<InferType<typeof chatMessageSchema>>
  watchRecipients: RecipientRow[] | undefined
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void
  onImagePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  images: ProcessedMedia[]
  onImageRemove: (image: ProcessedMedia) => void
  imageUploadDisabled: boolean
  resetForm: () => void
}

export const ChatMessageBar = ({
  handleSubmit,
  disabled,
  control,
  watchRecipients,
  onUploadImage,
  onImagePaste,
  images,
  onImageRemove,
  imageUploadDisabled,
  resetForm,
}: ChatMessageBarProps): React.ReactNode => {
  const [chatHeaderState, setChatHeaderState] = useAtom(chatHeaderStateAtom)
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { setSelectedChatRoomId } = useSelectedChatId()
  const { userId } = useAuth()
  const { data: currentUser } = useQuery(userQueries.getUser(userId))

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { mutate: mutateCreateChatRoom, isPending } = useMutation({
    // An existing one-on-one room is written to instead of creating a new one.
    mutationFn: async (variables: {
      existingRoomId?: string
      chatRoom: NChat.NCreateRoom.IRequestBody
      chatMessage: SendRoomMessageBody
    }) => {
      const roomId =
        variables.existingRoomId ??
        (await createRoom(variables.chatRoom)).data.data?._id
      if (!roomId) throw new Error('No room created')
      await sendRoomMessage(roomId, variables.chatMessage)
      return { roomId }
    },
    onSuccess: ({ roomId }) => {
      setChatHeaderState(ChatHeaderState.None)
      setSelectedChatRoomId(roomId)
      resetForm()
      queryClient.invalidateQueries({ queryKey: chatConversationsQueryKey })
      navigate(reverseUrl('chat:detail', { id: roomId }))
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      captureException(error)
    },
  })

  const toMessageBody = (
    data: InferType<typeof chatMessageSchema>
  ): SendRoomMessageBody => ({
    text: data.message ?? '',
    mediaIds: data.mediaIds ?? [],
  })

  const initiateNewBoardChat = (
    data: InferType<typeof chatMessageSchema>
  ): void => {
    if (!data.groupChatTitle) {
      return
    }
    const createRoomData: NChat.NCreateRoom.IRequestBody = {
      name: data.groupChatTitle,
      type: 'board',
      membersList: [],
      societyId: data.society as string,
    }

    mutateCreateChatRoom({
      chatRoom: createRoomData,
      chatMessage: toMessageBody(data),
    })
  }

  const initiateNewOneOnOneChat = (
    recipient: RecipientRow,
    data: InferType<typeof chatMessageSchema>
  ): void => {
    const createRoomData: NChat.NCreateRoom.IRequestBody = {
      name: `${getUserFullName(currentUser)} / ${recipientFullName(recipient)}`,
      societyId: recipient.societyId,
      type: 'oneonone',
      membersList: [{ userId: userId as string }, { userId: recipient.userId }],
    }

    mutateCreateChatRoom({
      existingRoomId: recipient.roomId,
      chatRoom: createRoomData,
      chatMessage: toMessageBody(data),
    })
  }

  const initiateNewGroupChat = (
    recipients: RecipientRow[],
    data: InferType<typeof chatMessageSchema>
  ): void => {
    if (!data.groupChatTitle || !data.society || !data.isPrivate || !userId) {
      return
    }
    const createRoomData: NChat.NCreateRoom.IRequestBody = {
      name: data.groupChatTitle,
      type: 'interests',
      membersList: recipients
        .map((recipient) => ({ userId: recipient.userId }))
        .concat([{ userId }]),
      societyId: data.society as string,
      isPrivate: data.isPrivate === 'true',
    }
    mutateCreateChatRoom({
      chatRoom: createRoomData,
      chatMessage: toMessageBody(data),
    })
  }

  const onSubmit = async (
    data: InferType<typeof chatMessageSchema>
  ): Promise<void> => {
    if (
      data.recipients?.length === 0 &&
      chatHeaderState !== ChatHeaderState.StartNewBoardChat
    ) {
      return
    }
    if (
      data.recipients?.length === 0 &&
      chatHeaderState === ChatHeaderState.StartNewBoardChat
    ) {
      initiateNewBoardChat(data)
      return
    }
    const selectedRecipients = data.recipients ?? []

    if (selectedRecipients.length === 1 && !data.isGroupchat) {
      initiateNewOneOnOneChat(selectedRecipients[0], data)
    } else if (selectedRecipients.length >= 1 && data.isGroupchat) {
      initiateNewGroupChat(selectedRecipients, data)
    }
  }

  const noRecipients = watchRecipients && watchRecipients.length === 0
  const shouldDisableSendButton =
    noRecipients &&
    (chatHeaderState === ChatHeaderState.StartNewChat ||
      chatHeaderState === ChatHeaderState.StartNewInterestsChat)

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex-none bg-neutral-98 p-4 md:py-4 md:px-4"
    >
      <div className="flex flex-1 gap-1 self-end">
        <MessageBarUpload
          name="mediaIds"
          control={control}
          imageUploadDisabled={imageUploadDisabled}
          onUploadImage={onUploadImage}
        />
        <div className="flex w-full flex-col rounded-xl bg-neutral-92">
          <MessageBarImages images={images} onImageRemove={onImageRemove} />
          <Controller
            control={control}
            name="message"
            render={({ field: { value, onChange, onBlur } }) => (
              <TextArea
                className="bg-neutral-92"
                roundedClassName="rounded-xl"
                placeholder="Aa"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onPaste={onImagePaste}
                rows={1}
                removeBorders
                fixedHeight={false}
                testId="chat-message-input"
              />
            )}
          />
        </div>
        <div className="flex self-end">
          <Button
            type="submit"
            data-testid="chat-send-button"
            disabled={disabled || shouldDisableSendButton || isPending}
            className="h-11 w-11 border-none bg-transparent"
            size="no-padding"
            iconSize={20}
            variant={ButtonVariant.TEXT}
            iconColor="black"
            icon={IconChoices.SEND}
          />
        </div>
      </div>
    </form>
  )
}
